import React, { useState, useEffect } from 'react';
import {
  Award,
  Plus,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Filter,
  Shield,
  Trash2,
  ChevronDown,
  AlertTriangle,
  FileCheck,
  BookOpen,
  Building2,
  CreditCard,
  GraduationCap,
  Users,
  Eye,
  CheckSquare,
} from 'lucide-react';
import {
  Siswa,
  Kelas,
  PosTagihan,
  BeasiswaPotongan,
  KategoriSiswa,
  StatusVerifikasi,
  JenisPotongan,
  TipePotongan,
  SppGradeRule,
} from '../../types/index.js';
import { useDialog } from '../ui/DialogProvider.js';

interface BeasiswaViewProps {
  siswaList: Siswa[];
  kelasList: Kelas[];
  posList: PosTagihan[];
  beasiswaList: BeasiswaPotongan[];
  sppRules?: SppGradeRule[];
  tahunAjaranAktif: string;
  currentUserLevel?: string;
  currentUsername?: string;
  onCreateBeasiswa: (payload: any) => Promise<any>;
  onVerifikasiBeasiswa: (id: string, payload: { statusVerifikasi: 'DISETUJUI' | 'DITOLAK'; catatanVerifikasi?: string; diverifikasiOleh?: string }) => Promise<any>;
  onBatchVerifikasiBeasiswa: (payload: { ids: string[]; statusVerifikasi: 'DISETUJUI' | 'DITOLAK'; catatanVerifikasi?: string; diverifikasiOleh?: string }) => Promise<any>;
  onDeleteBeasiswa: (id: string) => Promise<any>;
  onRefresh: () => Promise<void>;
}

const KATEGORI_LABELS: Record<KategoriSiswa, string> = {
  REGULER: 'Reguler',
  BEASISWA_PRESTASI: 'Beasiswa Prestasi',
  BEASISWA_YATIM: 'Beasiswa Yatim',
  ANAK_GURU: 'Anak Guru / Pegawai',
  AFIRMASI: 'Afirmasi Khusus',
};

const STATUS_CONFIG: Record<StatusVerifikasi, { label: string; bgColor: string; textColor: string; borderColor: string; icon: any }> = {
  MENUNGGU: { label: 'Menunggu Verifikasi', bgColor: 'bg-amber-50', textColor: 'text-amber-800', borderColor: 'border-amber-300', icon: Clock },
  DISETUJUI: { label: 'Disetujui', bgColor: 'bg-emerald-50', textColor: 'text-emerald-800', borderColor: 'border-emerald-300', icon: CheckCircle2 },
  DITOLAK: { label: 'Ditolak', bgColor: 'bg-rose-50', textColor: 'text-rose-800', borderColor: 'border-rose-300', icon: XCircle },
};

export const BeasiswaView: React.FC<BeasiswaViewProps> = ({
  siswaList,
  kelasList,
  posList,
  beasiswaList,
  sppRules = [],
  tahunAjaranAktif,
  currentUserLevel = 'SUPERADMIN',
  currentUsername = 'admin',
  onCreateBeasiswa,
  onVerifikasiBeasiswa,
  onBatchVerifikasiBeasiswa,
  onDeleteBeasiswa,
  onRefresh,
}) => {
  const { showAlert, showConfirm } = useDialog();
  const isKepsek = currentUserLevel === 'KEPALA_SEKOLAH' || currentUserLevel === 'SUPERADMIN';

  // Filters
  const [filterStatus, setFilterStatus] = useState<StatusVerifikasi | 'ALL'>('ALL');
  const [filterJenis, setFilterJenis] = useState<JenisPotongan | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showVerifikasiModal, setShowVerifikasiModal] = useState<string | null>(null);

  // Create form state
  const [formSiswaId, setFormSiswaId] = useState('');
  const [searchSiswaTerm, setSearchSiswaTerm] = useState('');
  const [isSiswaDropdownOpen, setIsSiswaDropdownOpen] = useState(false);
  const [formPosIds, setFormPosIds] = useState<string[]>([]);
  const [formJenis, setFormJenis] = useState<JenisPotongan>('MANUAL');
  const [formKategori, setFormKategori] = useState<KategoriSiswa>('REGULER');
  const [formTipe, setFormTipe] = useState<TipePotongan>('NOMINAL');
  const [formNilai, setFormNilai] = useState('0');
  const [formKeterangan, setFormKeterangan] = useState('');
  const [formBerlakuMulai, setFormBerlakuMulai] = useState('Juli 2026');
  const [formBerlakuSampai, setFormBerlakuSampai] = useState('Juni 2027');

  // Verifikasi form state
  const [verifStatus, setVerifStatus] = useState<'DISETUJUI' | 'DITOLAK'>('DISETUJUI');
  const [verifCatatan, setVerifCatatan] = useState('');

  // Filtered list
  const filteredList = beasiswaList.filter((bp) => {
    if (filterStatus !== 'ALL' && bp.statusVerifikasi !== filterStatus) return false;
    if (filterJenis !== 'ALL' && bp.jenisPotongan !== filterJenis) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !(bp.namaSiswa || '').toLowerCase().includes(q) &&
        !(bp.nis || '').includes(q) &&
        !(bp.namaPos || '').toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  // Stats
  const menungguCount = beasiswaList.filter(bp => bp.statusVerifikasi === 'MENUNGGU').length;
  const disetujuiCount = beasiswaList.filter(bp => bp.statusVerifikasi === 'DISETUJUI').length;
  const ditolakCount = beasiswaList.filter(bp => bp.statusVerifikasi === 'DITOLAK').length;

  const handleTogglePosId = (posId: string) => {
    setFormPosIds(prev =>
      prev.includes(posId) ? prev.filter(p => p !== posId) : [...prev, posId]
    );
  };

  const handleSelectAll = () => {
    const selectableIds = filteredList.filter(bp => bp.statusVerifikasi === 'MENUNGGU').map(bp => bp.id);
    if (selectedIds.length === selectableIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableIds);
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formSiswaId) { showAlert('Pilih siswa terlebih dahulu', { type: 'danger' }); return; }
    if (formPosIds.length === 0) { showAlert('Pilih minimal satu pos tagihan', { type: 'danger' }); return; }
    if (Number(formNilai) <= 0) { showAlert('Nilai potongan harus lebih dari 0', { type: 'danger' }); return; }

    try {
      const res = await onCreateBeasiswa({
        siswaId: formSiswaId,
        posIds: formPosIds,
        tahunAjaran: tahunAjaranAktif,
        jenisPotongan: formJenis,
        kategoriBeasiswa: formKategori,
        tipePotongan: formTipe,
        nilaiPotongan: Number(formNilai),
        keterangan: formKeterangan,
        berlakuMulai: formBerlakuMulai,
        berlakuSampai: formBerlakuSampai,
        diajukanOleh: currentUsername,
      });
      if (res?.success) {
        showAlert(res.message || 'Berhasil mengajukan potongan beasiswa', { type: 'success', title: 'Sukses' });
        setShowCreateModal(false);
        resetForm();
        await onRefresh();
      }
    } catch (err: any) {
      showAlert('Gagal: ' + (err?.message || 'Terjadi kesalahan'), { type: 'danger' });
    }
  };

  const handleVerifikasi = async (id: string) => {
    try {
      const res = await onVerifikasiBeasiswa(id, {
        statusVerifikasi: verifStatus,
        catatanVerifikasi: verifCatatan,
        diverifikasiOleh: currentUsername,
      });
      if (res?.success) {
        showAlert(res.message || 'Verifikasi berhasil', { type: 'success', title: 'Sukses' });
        setShowVerifikasiModal(null);
        setVerifCatatan('');
        await onRefresh();
      }
    } catch (err: any) {
      showAlert('Gagal: ' + (err?.message || 'Terjadi kesalahan'), { type: 'danger' });
    }
  };

  const handleBatchVerifikasi = async (status: 'DISETUJUI' | 'DITOLAK') => {
    if (selectedIds.length === 0) { showAlert('Pilih pengajuan terlebih dahulu', { type: 'danger' }); return; }
    const confirmMsg = `${status === 'DISETUJUI' ? 'Setujui' : 'Tolak'} ${selectedIds.length} pengajuan beasiswa yang dipilih?`;
    const ok = await showConfirm(confirmMsg, { type: status === 'DISETUJUI' ? 'warning' : 'danger', title: 'Batch Verifikasi' });
    if (!ok) return;

    try {
      const res = await onBatchVerifikasiBeasiswa({
        ids: selectedIds,
        statusVerifikasi: status,
        diverifikasiOleh: currentUsername,
      });
      if (res?.success) {
        showAlert(res.message || 'Batch verifikasi berhasil', { type: 'success', title: 'Sukses' });
        setSelectedIds([]);
        await onRefresh();
      }
    } catch (err: any) {
      showAlert('Gagal: ' + (err?.message || 'Terjadi kesalahan'), { type: 'danger' });
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await showConfirm('Hapus pengajuan beasiswa ini?', { type: 'danger', title: 'Hapus Pengajuan' });
    if (!ok) return;
    try {
      await onDeleteBeasiswa(id);
      await onRefresh();
    } catch (err: any) {
      showAlert(err?.message || 'Gagal menghapus', { type: 'danger' });
    }
  };

  const resetForm = () => {
    setFormSiswaId('');
    setFormPosIds([]);
    setFormJenis('MANUAL');
    setFormKategori('REGULER');
    setFormTipe('NOMINAL');
    setFormNilai('0');
    setFormKeterangan('');
  };

  const getPosIcon = (kode?: string) => {
    if (!kode) return <CreditCard className="w-3.5 h-3.5" />;
    if (kode === 'SPP' || kode === '002') return <CreditCard className="w-3.5 h-3.5" />;
    if (kode.includes('BUKU') || kode.includes('003')) return <BookOpen className="w-3.5 h-3.5" />;
    if (kode.includes('GEDUNG') || kode.includes('001')) return <Building2 className="w-3.5 h-3.5" />;
    return <FileCheck className="w-3.5 h-3.5" />;
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Header */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-slate-900">Manajemen Potongan Beasiswa</h2>
              {menungguCount > 0 && (
                <span className="bg-amber-100 text-amber-800 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full border border-amber-300 animate-pulse">
                  {menungguCount} Menunggu Verifikasi
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Pengajuan & verifikasi potongan beasiswa — Manual maupun Otomatis, bisa untuk SPP, Buku, Uang Gedung, dll
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Ajukan Potongan</span>
            </button>

            {isKepsek && selectedIds.length > 0 && (
              <>
                <button
                  onClick={() => handleBatchVerifikasi('DISETUJUI')}
                  className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs cursor-pointer"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Setujui ({selectedIds.length})</span>
                </button>
                <button
                  onClick={() => handleBatchVerifikasi('DITOLAK')}
                  className="flex items-center gap-1.5 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold px-3.5 py-1.5 rounded-md transition shadow-xs cursor-pointer"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  <span>Tolak ({selectedIds.length})</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-100">
          <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-semibold text-amber-800">Menunggu</span>
            </div>
            <p className="text-lg font-extrabold text-amber-900 mt-1">{menungguCount}</p>
          </div>
          <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-semibold text-emerald-800">Disetujui</span>
            </div>
            <p className="text-lg font-extrabold text-emerald-900 mt-1">{disetujuiCount}</p>
          </div>
          <div className="bg-rose-50 p-3 rounded-lg border border-rose-200">
            <div className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-rose-600" />
              <span className="text-xs font-semibold text-rose-800">Ditolak</span>
            </div>
            <p className="text-lg font-extrabold text-rose-900 mt-1">{ditolakCount}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="text-xs border border-slate-300 rounded-md px-3 py-1.5 bg-slate-50 font-medium"
            >
              <option value="ALL">Semua Status</option>
              <option value="MENUNGGU">Menunggu Verifikasi</option>
              <option value="DISETUJUI">Disetujui</option>
              <option value="DITOLAK">Ditolak</option>
            </select>
            <select
              value={filterJenis}
              onChange={(e) => setFilterJenis(e.target.value as any)}
              className="text-xs border border-slate-300 rounded-md px-3 py-1.5 bg-slate-50 font-medium"
            >
              <option value="ALL">Semua Jenis</option>
              <option value="MANUAL">Manual</option>
              <option value="SISTEM">Sistem (Otomatis)</option>
            </select>
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari NIS / Nama / Pos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 w-56"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
              <tr>
                {isKepsek && (
                  <th className="py-2.5 px-3 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.length > 0 && selectedIds.length === filteredList.filter(bp => bp.statusVerifikasi === 'MENUNGGU').length}
                      onChange={handleSelectAll}
                      className="rounded text-sky-600"
                    />
                  </th>
                )}
                <th className="py-2.5 px-3 w-12 text-center">No</th>
                <th className="py-2.5 px-3">Siswa</th>
                <th className="py-2.5 px-3">Pos Tagihan</th>
                <th className="py-2.5 px-3">Jenis</th>
                <th className="py-2.5 px-3">Kategori</th>
                <th className="py-2.5 px-3 text-right">Potongan</th>
                <th className="py-2.5 px-3">Periode</th>
                <th className="py-2.5 px-3">Pengaju</th>
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-3">Verifikator</th>
                <th className="py-2.5 px-3 text-center w-28">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredList.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-10 text-slate-400">
                    <Award className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="font-semibold">Belum ada data potongan beasiswa</p>
                    <p className="text-[11px]">Klik "Ajukan Potongan" untuk membuat pengajuan baru</p>
                  </td>
                </tr>
              ) : (
                filteredList.map((bp, idx) => {
                  const statusCfg = STATUS_CONFIG[bp.statusVerifikasi];
                  const StatusIcon = statusCfg.icon;

                  return (
                    <tr key={bp.id} className="hover:bg-sky-50/40 transition">
                      {isKepsek && (
                        <td className="py-2 px-3 text-center">
                          {bp.statusVerifikasi === 'MENUNGGU' ? (
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(bp.id)}
                              onChange={() => handleToggleSelect(bp.id)}
                              className="rounded text-sky-600"
                            />
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      )}
                      <td className="py-2 px-3 text-center text-slate-500 font-mono">{idx + 1}</td>
                      <td className="py-2 px-3">
                        <div className="font-bold text-slate-800">{bp.namaSiswa || bp.nis}</div>
                        <div className="text-[10px] text-slate-500">NIS: {bp.nis} • {bp.kelasNama}</div>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1.5">
                          {getPosIcon(bp.kodePos)}
                          <span className="font-semibold text-slate-800">{bp.namaPos || bp.posId}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold border ${
                          bp.jenisPotongan === 'MANUAL'
                            ? 'bg-sky-50 text-sky-800 border-sky-300'
                            : 'bg-violet-50 text-violet-800 border-violet-300'
                        }`}>
                          {bp.jenisPotongan}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <span className="text-[11px] text-slate-700 font-medium">
                          {bp.kategoriBeasiswa ? KATEGORI_LABELS[bp.kategoriBeasiswa] || bp.kategoriBeasiswa : '-'}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <span className="font-mono font-bold text-sky-900">
                          {bp.tipePotongan === 'PERSEN'
                            ? `${bp.nilaiPotongan}%`
                            : `Rp ${bp.nilaiPotongan.toLocaleString('id-ID')}`}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-[11px] text-slate-600">
                        {bp.berlakuMulai ? `${bp.berlakuMulai} – ${bp.berlakuSampai}` : '-'}
                      </td>
                      <td className="py-2 px-3 text-slate-600 text-[11px]">{bp.diajukanOleh}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border ${statusCfg.bgColor} ${statusCfg.textColor} ${statusCfg.borderColor}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusCfg.label}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-[11px] text-slate-600">
                        {bp.diverifikasiOleh || '-'}
                        {bp.catatanVerifikasi && (
                          <div className="text-[10px] text-slate-400 italic truncate max-w-[120px]" title={bp.catatanVerifikasi}>
                            {bp.catatanVerifikasi}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {isKepsek && bp.statusVerifikasi === 'MENUNGGU' && (
                            <button
                              onClick={() => { setShowVerifikasiModal(bp.id); setVerifStatus('DISETUJUI'); }}
                              className="px-1.5 py-0.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300 rounded text-[10px] font-semibold cursor-pointer"
                              title="Verifikasi pengajuan ini"
                            >
                              <Shield className="w-3 h-3 inline" /> Verif
                            </button>
                          )}
                          {bp.statusVerifikasi === 'MENUNGGU' && (
                            <button
                              onClick={() => handleDelete(bp.id)}
                              className="px-1.5 py-0.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-300 rounded text-[10px] font-semibold cursor-pointer"
                            >
                              <Trash2 className="w-3 h-3 inline" />
                            </button>
                          )}
                          {bp.statusVerifikasi !== 'MENUNGGU' && (
                            <span className="text-[10px] text-slate-400 italic">Selesai</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============================================ */}
      {/* MODAL: Ajukan Potongan Beasiswa Baru */}
      {/* ============================================ */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 border border-slate-200 animate-in fade-in my-8">
            <h3 className="text-sm font-bold text-slate-900 mb-4 pb-2 border-b flex items-center gap-2">
              <Award className="w-4 h-4 text-sky-600" />
              Ajukan Potongan Beasiswa Baru
            </h3>
            <form onSubmit={handleCreateSubmit} className="space-y-4 text-xs">
              {/* Siswa Selector (Searchable) */}
              <div className="relative">
                <label className="block text-slate-700 font-semibold mb-1">Pilih Siswa</label>
                <div 
                  className="w-full px-3 py-2 border rounded-md bg-white font-medium cursor-text flex justify-between items-center"
                  onClick={() => setIsSiswaDropdownOpen(true)}
                >
                  <input 
                    type="text" 
                    className="w-full outline-none"
                    placeholder="-- Ketik Nama atau NIS Siswa --"
                    value={searchSiswaTerm}
                    onChange={(e) => {
                      setSearchSiswaTerm(e.target.value);
                      setIsSiswaDropdownOpen(true);
                      if (e.target.value === '') {
                        setFormSiswaId('');
                        setFormKategori('REGULER');
                      }
                    }}
                    onFocus={() => setIsSiswaDropdownOpen(true)}
                  />
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </div>
                {isSiswaDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {siswaList
                      .filter(s => s.status === 'Aktif' && (s.nama.toLowerCase().includes(searchSiswaTerm.toLowerCase()) || s.nis.includes(searchSiswaTerm)))
                      .slice(0, 50) // Limit to 50 results for performance
                      .map((s) => (
                        <div 
                          key={s.id}
                          className="px-3 py-2 hover:bg-sky-50 cursor-pointer text-slate-700 border-b last:border-0"
                          onClick={() => {
                            setFormSiswaId(s.id);
                            setSearchSiswaTerm(`${s.nis} - ${s.nama} (${s.kelasNama})`);
                            if (s.kategori) setFormKategori(s.kategori);
                            setIsSiswaDropdownOpen(false);
                          }}
                        >
                          <div className="font-semibold">{s.nis} - {s.nama}</div>
                          <div className="text-[10px] text-slate-500">Kelas {s.kelasNama} • {KATEGORI_LABELS[s.kategori || 'REGULER']}</div>
                        </div>
                    ))}
                    {siswaList.filter(s => s.status === 'Aktif' && (s.nama.toLowerCase().includes(searchSiswaTerm.toLowerCase()) || s.nis.includes(searchSiswaTerm))).length === 0 && (
                      <div className="px-3 py-2 text-slate-500 italic text-center">Siswa tidak ditemukan</div>
                    )}
                  </div>
                )}
                {/* Overlay to close dropdown when clicking outside */}
                {isSiswaDropdownOpen && (
                  <div className="fixed inset-0 z-0" onClick={() => setIsSiswaDropdownOpen(false)}></div>
                )}
              </div>

              {/* Pos Tagihan Multi-Select */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1.5">
                  Pilih Pos Tagihan yang Diberi Potongan
                  <span className="text-[10px] text-slate-500 font-normal ml-1">(bisa pilih lebih dari satu)</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {posList.map((p) => {
                    const isSelected = formPosIds.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        className={`flex items-center gap-2.5 p-3 rounded-lg border-2 cursor-pointer transition ${
                          isSelected
                            ? 'bg-sky-50 border-sky-400 ring-1 ring-sky-300'
                            : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleTogglePosId(p.id)}
                          className="rounded text-sky-600 focus:ring-sky-500"
                        />
                        <div className="flex items-center gap-2">
                          {getPosIcon(p.kode)}
                          <div>
                            <div className={`font-semibold ${isSelected ? 'text-sky-900' : 'text-slate-700'}`}>{p.namaPos}</div>
                            <div className="text-[10px] text-slate-500">Kode: {p.kode} • {p.jenis} • Rp {p.defaultNominal.toLocaleString('id-ID')}</div>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Jenis & Kategori */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Jenis Potongan</label>
                  <select
                    value={formJenis}
                    onChange={(e) => setFormJenis(e.target.value as JenisPotongan)}
                    className="w-full px-3 py-2 border rounded-md bg-white font-medium"
                  >
                    <option value="MANUAL">Manual (Input Operator)</option>
                    <option value="SISTEM">Sistem (Otomatis dari Kategori)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Kategori Beasiswa</label>
                  <select
                    value={formKategori}
                    onChange={(e) => setFormKategori(e.target.value as KategoriSiswa)}
                    className="w-full px-3 py-2 border rounded-md bg-white font-medium"
                  >
                    {Object.entries(KATEGORI_LABELS).map(([key, label]) => {
                      // Find default potongan if SISTEM is selected
                      let hint = '';
                      if (formJenis === 'SISTEM') {
                        // Just look at the highest or average default for this category
                        const categoryRules = sppRules.filter(r => r.kategori === key);
                        if (categoryRules.length > 0) {
                          // Pick the max or first to give an idea
                          const maxPotongan = Math.max(...categoryRules.map(r => r.potongan));
                          if (maxPotongan > 0) {
                            hint = ` (Potongan Rp ${maxPotongan.toLocaleString('id-ID')})`;
                          }
                        }
                      }
                      return (
                        <option key={key} value={key}>{label}{hint}</option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Tipe & Nilai */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Tipe Potongan</label>
                  <select
                    value={formTipe}
                    onChange={(e) => setFormTipe(e.target.value as TipePotongan)}
                    className="w-full px-3 py-2 border rounded-md bg-white font-medium"
                  >
                    <option value="NOMINAL">Nominal (Rp)</option>
                    <option value="PERSEN">Persentase (%)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    Nilai Potongan {formTipe === 'PERSEN' ? '(%)' : '(Rp)'}
                  </label>
                  <input
                    type="number"
                    value={formNilai}
                    onChange={(e) => setFormNilai(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    min="0"
                    max={formTipe === 'PERSEN' ? '100' : undefined}
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Keterangan</label>
                  <input
                    type="text"
                    value={formKeterangan}
                    onChange={(e) => setFormKeterangan(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Alasan / keterangan"
                  />
                </div>
              </div>

              {/* Periode Berlaku */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Berlaku Mulai</label>
                  <input
                    type="text"
                    value={formBerlakuMulai}
                    onChange={(e) => setFormBerlakuMulai(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Juli 2026"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Berlaku Sampai</label>
                  <input
                    type="text"
                    value={formBerlakuSampai}
                    onChange={(e) => setFormBerlakuSampai(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Juni 2027"
                  />
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-[11px] text-amber-800">
                  <strong>Perhatian:</strong> Pengajuan potongan beasiswa ini akan <strong>menunggu verifikasi Kepala Sekolah</strong> sebelum diterapkan ke tagihan siswa.
                  Potongan hanya berlaku untuk tagihan yang belum dibayar pada pos tagihan yang dipilih.
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-4 py-1.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md font-medium"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-5 py-1.5 bg-sky-600 hover:bg-sky-500 text-white rounded-md font-semibold flex items-center gap-1.5"
                >
                  <Award className="w-3.5 h-3.5" />
                  Ajukan Potongan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL: Verifikasi oleh Kepala Sekolah */}
      {/* ============================================ */}
      {showVerifikasiModal && (() => {
        const bp = beasiswaList.find(b => b.id === showVerifikasiModal);
        if (!bp) return null;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 border border-slate-200 animate-in fade-in">
              <h3 className="text-sm font-bold text-slate-900 mb-4 pb-2 border-b flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-600" />
                Verifikasi Potongan Beasiswa
              </h3>

              {/* Info pengajuan */}
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 space-y-1.5 mb-4 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500">Siswa:</span>
                  <span className="font-bold text-slate-800">{bp.namaSiswa} ({bp.nis})</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Pos:</span>
                  <span className="font-semibold">{bp.namaPos}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Potongan:</span>
                  <span className="font-bold text-sky-900">
                    {bp.tipePotongan === 'PERSEN' ? `${bp.nilaiPotongan}%` : `Rp ${bp.nilaiPotongan.toLocaleString('id-ID')}`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Pengaju:</span>
                  <span className="font-medium">{bp.diajukanOleh}</span>
                </div>
                {bp.keterangan && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Keterangan:</span>
                    <span className="font-medium">{bp.keterangan}</span>
                  </div>
                )}
              </div>

              <div className="space-y-3 text-xs">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Keputusan</label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer transition ${
                      verifStatus === 'DISETUJUI' ? 'bg-emerald-50 border-emerald-400' : 'bg-slate-50 border-slate-200'
                    }`}>
                      <input
                        type="radio"
                        name="verifStatus"
                        value="DISETUJUI"
                        checked={verifStatus === 'DISETUJUI'}
                        onChange={() => setVerifStatus('DISETUJUI')}
                        className="text-emerald-600"
                      />
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span className="font-bold text-emerald-800">Setujui</span>
                    </label>
                    <label className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer transition ${
                      verifStatus === 'DITOLAK' ? 'bg-rose-50 border-rose-400' : 'bg-slate-50 border-slate-200'
                    }`}>
                      <input
                        type="radio"
                        name="verifStatus"
                        value="DITOLAK"
                        checked={verifStatus === 'DITOLAK'}
                        onChange={() => setVerifStatus('DITOLAK')}
                        className="text-rose-600"
                      />
                      <XCircle className="w-4 h-4 text-rose-600" />
                      <span className="font-bold text-rose-800">Tolak</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Catatan Verifikasi (Opsional)</label>
                  <textarea
                    value={verifCatatan}
                    onChange={(e) => setVerifCatatan(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    rows={2}
                    placeholder="Catatan dari Kepala Sekolah..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t mt-4">
                <button
                  type="button"
                  onClick={() => { setShowVerifikasiModal(null); setVerifCatatan(''); }}
                  className="px-4 py-1.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md font-medium text-xs"
                >
                  Batal
                </button>
                <button
                  onClick={() => handleVerifikasi(showVerifikasiModal)}
                  className={`px-5 py-1.5 rounded-md font-semibold text-xs flex items-center gap-1.5 text-white ${
                    verifStatus === 'DISETUJUI' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-rose-600 hover:bg-rose-500'
                  }`}
                >
                  <Shield className="w-3.5 h-3.5" />
                  {verifStatus === 'DISETUJUI' ? 'Setujui Potongan' : 'Tolak Pengajuan'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
